import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Outlet, createBrowserRouter, matchRoutes, useLocation, type RouteObject } from 'react-router';
import { Spinner } from '@moods/ui';
import { useAuth } from '../store/auth';
import { NotFoundPage, RouteErrorPage } from '../features/errors/ErrorPages';
import { postLoginPath } from '../features/auth/session';
import { SHELL_NAMESPACES, withNamespaces, type Namespace } from '../i18n';
import { MainSkeleton, ShellSkeleton } from '../shell/ShellSkeleton';

// Route-level code splitting (spec §15): every screen — the signed-in shell itself included — is a lazy chunk that
// loads its code and its i18n namespaces in parallel. `preloadRoute` starts the chunks of the URL being opened while
// the session refresh is still in flight, so the first paint does not wait on a waterfall.
// `test/i18n-namespaces.test.ts` checks every `page(…, [namespaces])` list against the screen's import graph.

type Screen = ComponentType<object>;
type Lazy = Screen & { preload: () => Promise<unknown> };

function page(load: () => Promise<{ default: Screen }>, ns: readonly Namespace[]): Lazy {
  let pending: Promise<{ default: Screen }> | null = null;
  const preload = () =>
    (pending ??= withNamespaces(load, ns)().catch((err: unknown) => {
      pending = null; // a failed chunk (offline, deploy) may be retried by the next navigation
      throw err;
    }));
  return Object.assign(lazy(preload), { preload });
}

const AppShell = page(() => import('../shell/AppShell').then(m => ({ default: m.AppShell })), SHELL_NAMESPACES);

const LoginPage = page(() => import('../features/auth/LoginPage').then(m => ({ default: m.LoginPage })), ['auth']);
const RegisterPage = page(() => import('../features/auth/RegisterPage').then(m => ({ default: m.RegisterPage })), ['auth']);
const flow = (name: 'ForgotPage' | 'MfaPage' | 'QrPage' | 'ResetPage' | 'VerifyDevicePage' | 'VerifyEmailPage') =>
  page(() => import('../features/auth/FlowPages').then(m => ({ default: m[name] })), ['auth']);
const ForgotPage = flow('ForgotPage');
const MfaPage = flow('MfaPage');
const QrPage = flow('QrPage');
const ResetPage = flow('ResetPage');
const VerifyDevicePage = flow('VerifyDevicePage');
const VerifyEmailPage = flow('VerifyEmailPage');
// accounts under the minimum age (DECISIONS › Minimum yaş 15) see only this notice
const AgeBlockPage = page(() => import('../features/auth/AgeBlockPage'), ['auth', 'settings']);

const OnboardingPage = page(() => import('../features/onboarding/OnboardingPage'), ['onboarding', 'discover', 'social', 'settings', 'notifications', 'mobile', 'community']);
const InvitePage = page(() => import('../features/invite/InvitePage'), ['community', 'chat']);
const DevUiPage = page(() => import('../features/dev/DevUiPage'), ['chat']);

const HomePage = page(() => import('../features/home/HomePage'), ['home']);
const CommunityHomePage = page(() => import('../features/community/CommunityHomePage'), ['community', 'voice', 'chat', 'events']);
const ChannelBrowserPage = page(() => import('../features/community/ChannelBrowserPage'), ['community']);
// Entegrasyonlar reuses the Community Settings webhook section (its copy is in `communitySettings`)
const ChannelSettingsPage = page(() => import('../features/community/ChannelSettingsPage'), ['community', 'communitySettings']);
const CommunitySettingsPage = page(() => import('../features/community-settings/CommunitySettingsPage'), ['community', 'communitySettings', 'safety', 'moderation', 'chat', 'discover', 'profile']);
const ChannelPage = page(() => import('../features/channel/ChannelPage'), ['community', 'social', 'chat', 'threads', 'search']);
const SettingsPage = page(() => import('../features/settings/SettingsPage'), ['settings', 'communitySettings']);
const FriendsPage = page(() => import('../features/friends/FriendsPage'), ['social']);
const DmHome = page(() => import('../features/dms/DmHome'), ['social']);
const DmPage = page(() => import('../features/dms/DmPage'), ['community', 'social', 'chat', 'threads', 'profile', 'search', 'voice']);
const ProfilePage = page(() => import('../features/profile/ProfilePage'), ['social', 'voice']);
const StaffPage = page(() => import('../features/staff/StaffPage'), ['community', 'chat', 'communitySettings', 'moderation', 'staff', 'discover', 'safety']);
const EventsPage = page(() => import('../features/events/EventsPage'), ['community', 'events', 'chat']);
const CommunityEventsPage = page(() => import('../features/events/CommunityEventsPage'), ['community', 'events', 'chat']);
const BoardsPage = page(() => import('../features/boards/BoardsPage'), ['boards', 'community', 'chat', 'communitySettings']);
// "Tüm görevlerim": my cards on every board of every community (opens the board's card dialog in place)
const MyTasksPage = page(() => import('../features/boards/MyTasksPage'), ['boards', 'community', 'chat', 'communitySettings']);
const InboxPage = page(() => import('../features/inbox/InboxPage'), ['chat']);
const MarketPage = page(() => import('../features/market/MarketPage'), ['market']);
// Sinema Salonu (docs/cinema): the salon and its chat (the channel's own messages)
const SalonPage = page(() => import('../features/cinema/SalonPage'), ['cinema', 'community', 'social', 'chat', 'threads', 'profile', 'search', 'voice']);
// MOODS Arena: Takım Bul + the team room (a group DM chat with its call: the DM page's namespaces)
const ArenaPage = page(() => import('../features/arena/ArenaPage'), ['arena', 'market', 'community', 'social', 'chat', 'threads', 'profile', 'search', 'voice']);
const SavedPage = page(() => import('../features/saved/SavedPage'), ['chat']);
const DiscoverPage = page(() => import('../features/discover/DiscoverPage'), ['discover']);
const CommunityPreviewPage = page(() => import('../features/discover/CommunityPreviewPage'), ['community', 'discover', 'chat']);
// Phase 11: the phone bottom-navigation destinations Topluluklar / Bildirimler (usable at any width)
const CommunityListPage = page(() => import('../features/community/CommunityListPage'), ['community']);
const NotificationsPage = page(() => import('../features/notifications/NotificationsPage'), []);

export function FullPageLoader() {
  return (
    <div className="boot" role="status" aria-live="polite">
      <Spinner size={22} />
    </div>
  );
}

/** Screens outside the shell (auth, invite, onboarding): a small boot indicator while the chunk loads. */
const standalone = (C: Lazy) => (
  <Suspense fallback={<FullPageLoader />}>
    <C />
  </Suspense>
);
/** Screens inside the shell: the content skeleton while the chunk loads (no spinners in content areas, §11.8). */
const inShell = (C: Lazy) => (
  <Suspense fallback={<MainSkeleton />}>
    <C />
  </Suspense>
);

interface RouteHandle {
  preload?: () => Promise<unknown>;
  /** needs a session — only preloaded when one is likely (see `preloadRoute`) */
  auth?: boolean;
}
const screen = (path: string, C: Lazy, auth = true): RouteObject => ({
  path,
  element: auth ? inShell(C) : standalone(C),
  handle: { preload: C.preload, auth } satisfies RouteHandle,
});

/** Requires a session; while the refresh-cookie bootstrap runs, shows the loader; otherwise → /login?next=. */
function RequireAuth() {
  const status = useAuth(s => s.status);
  const ageBlocked = useAuth(s => Boolean(s.me?.ageBlock));
  const location = useLocation();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'anonymous') return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  // under 15: the notice instead of the shell (no gateway, no other screens)
  if (ageBlocked) return standalone(AgeBlockPage);
  return <Outlet />;
}

/** Auth screens redirect signed-in users home (except flows that work either way). */
function AnonymousOnly() {
  const status = useAuth(s => s.status);
  const location = useLocation();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'authenticated') {
    // just signed in/up here: honour ?next= (or the MFA state's next), new accounts go to onboarding
    const params = new URLSearchParams(location.search);
    const stateNext = (location.state as { next?: string | null } | null)?.next ?? null;
    const target = location.pathname === '/register' ? (params.get('next') ?? '/onboarding') : postLoginPath(params.get('next') ?? stateNext);
    return <Navigate to={target} replace />;
  }
  return <Outlet />;
}

const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/', element: <Navigate to="/home" replace /> },
      {
        element: <AnonymousOnly />,
        children: [screen('/login', LoginPage, false), screen('/register', RegisterPage, false), screen('/forgot', ForgotPage, false), screen('/mfa', MfaPage, false)],
      },
      screen('/reset', ResetPage, false),
      screen('/verify-email', VerifyEmailPage, false),
      screen('/verify-device', VerifyDevicePage, false),
      screen('/qr', QrPage, false),
      // join preview works signed out too (the page signs in / joins itself)
      screen('/invite/:code', InvitePage, false),
      // the gallery: dev server, or a build made with VITE_DEV_UI=1 (e2e against `vite build` + `vite preview`)
      ...(import.meta.env.DEV || import.meta.env.VITE_DEV_UI === '1' ? [screen('/dev/ui', DevUiPage, false)] : []),
      {
        element: <RequireAuth />,
        children: [
          { path: '/onboarding/*', element: standalone(OnboardingPage), handle: { preload: OnboardingPage.preload, auth: true } satisfies RouteHandle },
          {
            element: (
              <Suspense fallback={<ShellSkeleton />}>
                <AppShell />
              </Suspense>
            ),
            handle: { preload: AppShell.preload, auth: true } satisfies RouteHandle,
            children: [
              screen('/home', HomePage),
              screen('/communities', CommunityListPage),
              screen('/notifications', NotificationsPage),
              screen('/c/:communityId', CommunityHomePage),
              screen('/c/:communityId/channels', ChannelBrowserPage),
              screen('/c/:communityId/channels/:channelId/settings/:section?', ChannelSettingsPage),
              screen('/c/:communityId/settings/:section?', CommunitySettingsPage),
              screen('/c/:communityId/events/:eventId?', CommunityEventsPage),
              screen('/c/:communityId/boards/:boardId?', BoardsPage),
              screen('/c/:communityId/:channelId/*', ChannelPage),
              screen('/friends', FriendsPage),
              screen('/messages', DmHome),
              screen('/messages/:channelId/:messageId?', DmPage),
              screen('/events', EventsPage),
              screen('/tasks', MyTasksPage),
              screen('/saved', SavedPage),
              screen('/inbox', InboxPage),
              screen('/discover', DiscoverPage),
              screen('/discover/c/:slug', CommunityPreviewPage),
              screen('/discover/:category', DiscoverPage),
              screen('/u/:username', ProfilePage),
              screen('/staff/*', StaffPage),
              // MOD ekonomisi: the Market and "MOD nasıl kazanılır" (404 for people without a wallet in the current mode)
              screen('/market/*', MarketPage),
              screen('/cinema/:salonId', SalonPage),
              // MOODS Arena (docs/arena): 404 while Yönetim paneli › Özellik anahtarları › Arena is off
              screen('/arena/*', ArenaPage),
              // User Settings render full-screen inside AppShell (the gateway stays connected; see DECISIONS Phase 10 — Web)
              // phones show the section list at /settings (Phase 11); wider screens redirect to Hesabım inside the page
              screen('/settings', SettingsPage),
              screen('/settings/:section', SettingsPage),
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);

/**
 * Starts loading the chunks (code + namespaces) of every route matched by `pathname`. Signed-in screens only when a
 * session is likely (`signedIn`), so a signed-out visitor of `/` downloads the login screen, not the shell.
 */
export function preloadRoute(pathname: string, signedIn: boolean): void {
  for (const m of matchRoutes(routes, pathname) ?? []) {
    const handle = m.route.handle as RouteHandle | undefined;
    if (handle?.preload && (signedIn || !handle.auth)) void handle.preload().catch(() => undefined);
  }
}
